import React, {useMemo, useCallback, useEffect, useRef, useState} from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useLocation } from 'react-router-dom';
import {usePersistedDataStore, useCompStore} from '../store';
import AdjustmentsSummaryDisplay from './AdjustmentsSummaryDisplay';
import CompNavigation from './CompNavigation';
import Tooltip from '@mui/material/Tooltip';
import {NumericFormat} from "react-number-format";
import AddIcon from '@mui/icons-material/Add';
import { blue } from '@mui/material/colors';
import SettingsIcon from '@mui/icons-material/Settings';
import AdvancedSettings, { loadAdvancedSettingsFromStorage } from './AdvancedSettings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useUser, userObject } from '../UserContext';
import ToolbarValueDisplay from './ToolbarValueDisplay';
import CaseStrengthWidget from './CaseStrengthWidget';
// import CaseTimer from './CaseTimer';
import dayjs from 'dayjs';
import { calculateWeightedAverage } from '../../common/mappings';
import { pickMapInventory } from './Map/tooltipHelpers';
import { getCompAdjustedPrice } from '../../utils/adjustmentCalculator';
import { getParcelAdjustments } from '../../utils/getParcelAdjustments';
import { getIFMVUtil } from '../../utils/getAVUtil';
import SvgIconButton from './SvgIconButton';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { getConversionFactor } from '../../utils/conversionUtils';
import { getMostRecentOfferValue } from '../../utils/mostRecentOfferUtils';
import logger from '../../utils/logger';
import { findCompIndex } from './common/compSheetLookup.js';
import { subjectSalePrice } from './SummaryCaseTable/services/summaryRowCreationService.js';

export const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, value, inputRef, ...other } = props;
  return (
    <NumericFormat
      {...other}
      // getInputRef={ref}
      getInputRef={inputRef}
      onFocus={(e) =>
          e.currentTarget.setSelectionRange(
          e.currentTarget.value.length,
          e.currentTarget.value.length
      )}
      value={value}
      thousandSeparator
      allowNegative={false}
      isAllowed={(values) => {
        // Prevent negative values and ensure only valid positive numbers
        const { floatValue, formattedValue } = values;
        
        // Allow empty input
        if (floatValue === undefined) return true;
        
        // Prevent negative values
        if (floatValue < 0) return false;
        
        // Prevent just a minus sign
        if (formattedValue === '-') return false;
        
        return true;
      }}
      onValueChange={(values, sourceInfo) => {
        // Only bubble "onChange" for real user edits, not programmatic updates
        if (sourceInfo?.source !== 'event') return;
        onChange({ target: { value: values.value } });
      }}
    />
  );
});

// MUI <Tabs> cloneElement-injects value, selected, onChange, onClick, ref, etc. — forward all to <Tab>.
const DisabledTownTabWithHover = React.forwardRef(function DisabledTownTabWithHover(
  { tabSx, ...rest },
  ref
) {
  const label = (
    <span>
      <span className="town-label-text">Town</span>
      <AddIcon className="town-label-icon" sx={{ fontSize: 18, color: 'currentColor' }} />
    </span>
  );
  return (
    <Tab
      ref={ref}
      {...rest}
      title="Add Town comps"
      label={label}
      sx={[
        tabSx,
        {
          color: 'text.disabled',
          '& .MuiTab-wrapper': { color: 'inherit' },
          '& .town-label-text': { display: 'inline' },
          '& .town-label-icon': { display: 'none', color: 'currentColor' },
          '&:hover': {
            color: blue[700],
            backgroundColor: '#f0f0f0',
            cursor: 'pointer',
            opacity: 1,
            WebkitTextFillColor: blue[700],
            '& .town-label-text': { display: 'none' },
            '& .town-label-icon': { display: 'inline-flex' },
          },
          '&.Mui-selected': {
            color: blue[700],
            WebkitTextFillColor: blue[700],
          },
        },
      ]}
    />
  );
});

const TableCustomToolbar = ({ 
  subject, 
  subjectIFMV,
  view, 
  handleProcessDBUpdate, 
  viewCallback, 
  regularComps,
  memoizedSelectedComps,
  savedComps,
  negotiationCompsValues,
  caseNotes, 
  reviewDetails,
  onCompChange,
  onCaseNotesChanged,
  onCaseInfoClick,
  caseId,
  evalDate = null,
  isLoadingEvalDate = false,
  isSettled = false,
  townCompParcelIds = [],
}) => {

  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const getCompSheet = usePersistedDataStore((state) => state.compSheet);
  const getCaseNotes = usePersistedDataStore((state) => state.caseNotes);

  // subjectIFMV is now passed as a stable primitive prop from CaseReview
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const comp = useMemo(() => parseInt(queryParams.get('comp')) - 1 || 0, [queryParams]);

  const caseNotesIndex = useMemo(() => {
    const pid = subject?.parcel_id;
    const cases = getNegotiationObj?.cases;
    if (!pid || !cases?.length) return comp;
    const idx = cases.findIndex((c) => c.parcel_id === pid);
    return idx >= 0 ? idx : comp;
  }, [subject?.parcel_id, getNegotiationObj?.cases, comp]);
  
  useCompStore((state)=> state.adjustments)
  const getEvalDate = usePersistedDataStore((state) => state.evalDate);
  const [selectedKey, setSelectedKey] = useState("selectedComps");
  const offerInputRef = useRef(null);
  const townOfferInputRef = useRef(null);
  
  const currentCase = getNegotiationObj?.cases[comp];
  if (!currentCase) throw new Error('[TableCustomToolbar] Missing current case.');
  const getRar = currentCase.RAR;
  const getEq = currentCase.EQ;
  const mostRecentAventineOffer = getMostRecentOfferValue(currentCase?.Negotiations, 'Aventine');
  const mostRecentTownOffer = getMostRecentOfferValue(currentCase?.Negotiations, 'Town');
  
  // Use most recent values from negotiations, fallback to case-level values (same logic as SummaryCaseTable)
  const displayOfferValue = mostRecentAventineOffer !== null ? mostRecentAventineOffer : null;
  const displayTownOfferValue = mostRecentTownOffer !== null ? mostRecentTownOffer : null;
  useUser();

  const hasTownTab = townCompParcelIds?.length > 0;
  const tabItems = useMemo(() => {
    return [
      { id: 'regular', label: 'Analysis', callbackValue: 0 },
      { id: 'optimized', label: 'Court', callbackValue: 1 },
      { id: 'town', label: 'Town', callbackValue: 2 },
    ];
  }, []);

  const selectedTabIndex = useMemo(() => {
    const wanted = { regular: 'regular', optimized: 'optimized', town: 'town' }[view] || 'regular';
    const idx = tabItems.findIndex((t) => t.id === wanted);
    return idx >= 0 ? idx : 0;
  }, [view, tabItems]);

  const resolvedCaseNotes = useMemo(() => {
    const fromStore = getCaseNotes?.[caseNotesIndex];
    if (fromStore !== undefined) return fromStore ?? '';
    return getNegotiationObj?.cases?.[caseNotesIndex]?.CaseNotes ?? '';
  }, [getCaseNotes, caseNotesIndex, getNegotiationObj?.cases]);
  const hasCaseNotes = useMemo(
    () => typeof resolvedCaseNotes === 'string' && resolvedCaseNotes.trim().length > 0,
    [resolvedCaseNotes]
  );
  const hasOperatorMemos = useMemo(
    () => Array.isArray(currentCase?.Memos) && currentCase.Memos.some((entry) => entry?.memo && String(entry.memo).trim()),
    [currentCase?.Memos]
  );
  const hasHomeownerNotes = useMemo(
    () => currentCase?.HomeownerNotes != null && String(currentCase.HomeownerNotes).trim().length > 0,
    [currentCase?.HomeownerNotes]
  );
  const hasAnyNotes = hasCaseNotes || hasOperatorMemos || hasHomeownerNotes;
  const notesTooltip = [
    hasCaseNotes && 'Internal Case Notes',
    hasOperatorMemos && 'Operator Memos',
    hasHomeownerNotes && 'Homeowner Notes',
  ].filter(Boolean).join(' and ') || 'Add Internal Note';
  const notesTooltipTitle = hasAnyNotes ? `Has ${notesTooltip}` : notesTooltip;

  // Handle click on notes icon to open property record card for subject
  const handleNotesIconClick = useCallback(() => {
    if (!subject || !onCaseInfoClick) return;
    
    // Create a formatted property object for the subject (similar to handleAddressClick in CaseReview.js)
    const formattedSubjectProperty = {
      address: subject.PropertyInfo?.Address || subject.Address,
      parcel_id: subject.parcel_id,
      salePrice: subject.SalePrice,
      saleDate: subject.SaleDate,
      adjustedPrice: subject.adj_price,
      ifmv: subject.IFMV,
      ...pickMapInventory(subject),
      MLSNumber: subject.MLSNumber,
      Remarks: subject.Remarks,
      SDName: subject.SDName,
      SDCode: subject.SDCode,
      town: subject.Town,
      zip: subject.ZipCode,
      isSubject: true, // This is the subject property
      selectedRowNumber: 0,
      latitude: subject.latitude || subject.Latitude,
      longitude: subject.longitude || subject.Longitude, // backwards compatible, dont recall whcih it is.
      focusInternalCaseNotes: hasCaseNotes,
      focusOperatorMemos: !hasCaseNotes && hasOperatorMemos,
      focusHomeownerNotes: !hasCaseNotes && !hasOperatorMemos && hasHomeownerNotes,
    };
    
    // Open the property record card
    onCaseInfoClick(formattedSubjectProperty);
  }, [subject, onCaseInfoClick, hasCaseNotes, hasOperatorMemos, hasHomeownerNotes]);


  const handleSelectChange = (event) => {
    setSelectedKey(event.target.value);
  };

  const [advancedSettings, setAdvancedSettings] = useState(loadAdvancedSettingsFromStorage);

  // Helper function to determine raw price field based on report type
  const getRawPriceField = useCallback((comp) => {
    const reportType = getNegotiationObj?.reportType;
    return reportType === 'assessment' ? comp.IFMV : comp.SalePrice;
  }, [getNegotiationObj?.reportType]);
  
    // Calculate comp averages
  const compAvg = useMemo(() => {
    const salePrice = subjectSalePrice(currentCase, regularComps);
    if (salePrice != null) {
      return {
        selectedComps: salePrice,
        savedComps: salePrice,
        top5: salePrice,
        selectedMedian: salePrice,
        selectedCompsCount: 0,
      };
    }
    
    if(view === 'town'){
      // Calculate average for town view using the raw comps data
      if (!negotiationCompsValues?.comps?.length) {
        return { selectedComps: NaN, selectedMedian: NaN };
      }
      
      // Calculate adjusted prices for negotiation comps using the same logic as regular comps
      const adjPrices = negotiationCompsValues.comps.map((compObj) => {
        const rawSalePrice = getRawPriceField(compObj);
        
        // Use the same adjustment calculation as regular comps
        const adjustedPrice = getCompAdjustedPrice({
          subject: subject,
          comp: compObj,
          salePrice: rawSalePrice,
          miscAdjustments: currentCase?.MiscAdjustments || {},
          savedComps: savedComps,
          currentCompId: getNegotiationObj?.reportType === 'assessment'
            ? compObj.parcel_id
            : (compObj.TransactionID ?? compObj.parcel_id),
          basePrice: rawSalePrice,
          logType: 'NEGOTIATION_TOOLBAR'
        });
        
        return adjustedPrice;
      }).filter((price) => price !== null && price !== undefined && !Number.isNaN(price));
      
      if (adjPrices.length === 0) {
        return { selectedComps: NaN, selectedMedian: NaN };
      }
      
      const negotiationAvg = calculateWeightedAverage(adjPrices);
      const sortedPrices = [...adjPrices].sort((a, b) => a - b);
      const mid = Math.floor(sortedPrices.length / 2);
      const negotiationMedian = sortedPrices.length % 2 === 0
        ? (sortedPrices[mid - 1] + sortedPrices[mid]) / 2
        : sortedPrices[mid];
      
      return {
        selectedComps: negotiationAvg,
        selectedMedian: negotiationMedian,
      }
    }

    // Helper function to get selected prices for comps
    const getSelectedPrices = (compIds, trace) => {
      if (!compIds || compIds.length === 0 || !regularComps?.parcel_id?.length) {
        return [];
      }
      
      const reportType = getNegotiationObj?.reportType || 'standard';
      // Find the indices of selected comps in the regularComps arrays
      const selectedIndices = [];
      compIds.forEach(compId => {
        const index = findCompIndex(regularComps, compId, reportType);
        if (index !== -1) {
          selectedIndices.push(index);
        } else if (trace) {
          trace.push({ requestedId: compId, sheetIndex: -1, missing: true });
        }
      });
      
      if (selectedIndices.length === 0) {
        return [];
      }
      
      // Get the adj_price values for selected comps using unified function
      const selectedPrices = selectedIndices.map(index => {
        // Create a comp object from the regularComps data at this index
        const comp = {
          parcel_id: regularComps.parcel_id[index],
          SalePrice: regularComps.SalePrice?.[index],
          IFMV: regularComps.IFMV?.[index],
          adj_price: regularComps.adj_price?.[index],
          // Add other fields as needed for the adjustment calculation
          ...Object.fromEntries(
            Object.keys(regularComps).map(key => [key, regularComps[key]?.[index]])
          )
        };
        
        const rawSalePrice = getRawPriceField(comp);
        const currentCompId = reportType === 'assessment'
          ? comp.parcel_id
          : (comp.TransactionID ?? comp.parcel_id);
        const miscAdjustments = currentCase?.MiscAdjustments || {};
        
        // Use unified adjustment calculation function - start from raw sale price
        const adjustedPrice = getCompAdjustedPrice({
          subject: subject, // Use actual subject data
          comp: comp,
          salePrice: rawSalePrice,
          miscAdjustments,
          savedComps: savedComps,
          currentCompId,
          basePrice: rawSalePrice, // Start with raw sale price, not adj_price
          logType: 'TOOLBAR_AVERAGE'
        });

        trace?.push({
          sheetIndex: index,
          parcel_id: comp.parcel_id,
          TransactionID: comp.TransactionID,
          currentCompId,
          rawPrice: rawSalePrice,
          storedAdjPrice: reportType === 'assessment' ? comp.adj_IFMV : comp.adj_price,
          basePrice: rawSalePrice,
          miscAdjustments,
          adjustedPrice,
        });
        
        return adjustedPrice;
      }).filter(price => price !== null && price !== undefined);
      
      return selectedPrices;
    };

    // Calculate average for selected comps 
    const calculateAverage = (compIds, trace) => {
      const selectedPrices = getSelectedPrices(compIds, trace);
      
      if (selectedPrices.length === 0) {
        return 'None selected';
      }
      
      const average = calculateWeightedAverage(selectedPrices);
      return average;
    };

    // Calculate median for selected comps
    const calculateMedian = (compIds) => {
      const selectedPrices = getSelectedPrices(compIds);
      
      if (selectedPrices.length === 0) {
        return 'None selected';
      }
      
      // Sort prices in ascending order
      const sortedPrices = [...selectedPrices].sort((a, b) => a - b);
      const mid = Math.floor(sortedPrices.length / 2);
      
      // If even number of values, return average of two middle values
      // If odd number, return middle value
      const median = sortedPrices.length % 2 === 0
        ? (sortedPrices[mid - 1] + sortedPrices[mid]) / 2
        : sortedPrices[mid];
      
      return median;
    };

    // Calculate averages based on selection
    const selectedAvgTrace = [];
    const selectedCompIds = Array.isArray(memoizedSelectedComps)
      ? memoizedSelectedComps
      : savedComps.length > 0
        ? savedComps
        : null;
    const selectedAverage = selectedCompIds
      ? calculateAverage(selectedCompIds, selectedAvgTrace)
      : 'None selected';


    const savedAverage = savedComps.length > 0
      ? calculateAverage(savedComps)
      : 'None selected';

    // Calculate median based on selection
    const selectedMedian = Array.isArray(memoizedSelectedComps)
      ? calculateMedian(memoizedSelectedComps)
      : savedComps.length > 0
      ? calculateMedian(savedComps)
      : 'None selected';

    // Calculate top5 average
    if (!regularComps?.parcel_id?.length) return {
      selectedComps: selectedAverage,
      savedComps: savedAverage,
      top5: 0,
      selectedMedian: selectedMedian
    };

    // Get top 5 comps using the same unified adjustment calculation as selected comps
    const top5Indices = Array.from({ length: Math.min(5, regularComps.parcel_id.length) }, (_, i) => i);
    const top5Prices = top5Indices.map(index => {
      // Create a comp object from the regularComps data at this index
      const comp = {
        parcel_id: regularComps.parcel_id[index],
        SalePrice: regularComps.SalePrice?.[index],
        IFMV: regularComps.IFMV?.[index],
        adj_price: regularComps.adj_price?.[index],
        // Add other fields as needed for the adjustment calculation
        ...Object.fromEntries(
          Object.keys(regularComps).map(key => [key, regularComps[key]?.[index]])
        )
      };
      
      const rawSalePrice = getRawPriceField(comp);
      
      // Use unified adjustment calculation function - start from raw sale price
      const adjustedPrice = getCompAdjustedPrice({
        subject: subject, // Use actual subject data
        comp: comp,
        salePrice: rawSalePrice,
        miscAdjustments: currentCase?.MiscAdjustments || {},
        savedComps: savedComps,
        currentCompId: getNegotiationObj?.reportType === 'assessment'
          ? comp.parcel_id
          : (comp.TransactionID ?? comp.parcel_id),
        basePrice: rawSalePrice, // Start with raw sale price, not adj_price
        logType: 'TOOLBAR_TOP5'
      });
      
      return adjustedPrice;
    }).filter(price => price !== null && price !== undefined);
    
    const top5Average = top5Prices.length > 0
      ? calculateWeightedAverage(top5Prices)
      : 0;

    const finalResult = {
      selectedComps: selectedAverage,
      savedComps: savedAverage,
      top5: top5Average,
      selectedMedian: selectedMedian,
      selectedCompsCount: Array.isArray(memoizedSelectedComps) ? memoizedSelectedComps.length : savedComps.length,
    };
    
    return finalResult;
  }, [regularComps, memoizedSelectedComps, savedComps, view, negotiationCompsValues, currentCase, getRawPriceField, subject, getNegotiationObj?.reportType]);

  const handleSettingsConfirm = (newSettings) => {
    setAdvancedSettings(newSettings);
  };
  
  const parcelId = subject?.parcel_id;
  const summaryAdjustments = useMemo(() => getParcelAdjustments(parcelId), [parcelId]);

  const memoizedSqftAdjustment = useMemo(() => {
    // Check if the Sqft adjustment is "IFMV Linked"
    if (summaryAdjustments?.Sqft === "IFMV Linked") {
      // Access the IFMV value for the current comp
      const ifmvValue = getIFMVUtil(getNegotiationObj.cases[comp]);
  
      if (ifmvValue) {
        // Calculate the adjustment factor based on IFMV value
        const sqftAdjustmentFactor = ifmvValue / 10000;
        return Math.round(sqftAdjustmentFactor); // Return the calculated adjustment
      } else {
        logger.warn("IFMV value not found for the comp. Returning null.");
        return null;
      }
    }
    // Otherwise, return the direct Sqft adjustment value
    return summaryAdjustments?.Sqft;
  }, [summaryAdjustments, comp, getNegotiationObj]);

  const handleClickOpen = () => {
    setOpen(true);
  };


  const handleClose = () => {
    setOpen(false);
  };

  const [open, setOpen] = useState(false);





  // Reset case notes changed flag when comp changes (new case)
  useEffect(() => {
    onCaseNotesChanged?.(false);
  }, [comp, onCaseNotesChanged]);



  const debouncedOfferPct = useMemo(() => {
    if (displayOfferValue) {
      const IFMV = getIFMVUtil(getNegotiationObj.cases[comp], 0);
      let numericValue = parseFloat(displayOfferValue.toString().replace(/,/g, ''));
      
      // Convert AV value to MV for percentage calculation if needed
      // Since we now store AV values, we need to convert to MV for proper percentage calculation
      const conversionFactor = getConversionFactor(getEq, getRar);
      if (conversionFactor > 0) {
        numericValue = numericValue / conversionFactor;
      }
      
      if (!isNaN(numericValue) && numericValue > 0) {
        return (numericValue - IFMV) / IFMV;
      }
    }
    return null;
  }, [displayOfferValue, comp, getNegotiationObj, getRar, getEq]);

  const handleCompChangeWithSync = useCallback(() => {
    onCompChange?.();
  }, [onCompChange]);

  const pctAdj = useMemo(() => {
    // Get compSheet entry for current case
    const compSheet = Array.isArray(getCompSheet) && getCompSheet.length > comp ? getCompSheet[comp] : null;
    
    // Get IFMV from compSheet (compSheet IFMV is array-based, use index 0)
    const IFMV = compSheet ? getIFMVUtil(compSheet, 0) : null;
    
    // Guard against invalid IFMV (null, undefined, or zero)
    if (!IFMV || IFMV === 0) {
      return 0;
    }
    
    // Select the appropriate average based on selectedKey
    let selectedAvg;
    if (selectedKey === "selectedComps") {
      selectedAvg = compAvg.selectedComps;
    } else if (selectedKey === "selectedMedian") {
      selectedAvg = compAvg.selectedMedian;
    } else {
      selectedAvg = compAvg.top5;
    }
  
    // Handle cases where selectedAvg might be a string
    const avg = typeof selectedAvg === "string" ? 0 : selectedAvg;
    
    // Guard against invalid average
    if (!avg || avg === 0) {
      return 0;
    }
  
    return (avg - IFMV) / IFMV; // Flipped formula for other views
  }, [compAvg, comp, selectedKey, getCompSheet]);

  const pctAdjClass = pctAdj < 0 ? 'text-green-700' : 'text-red-700';



  const formatEvalDate = (dateString) => {
    if (!dateString) return 'Never'; // Handle case where date is null or undefined
    const [year, month, day] = dateString.split('-'); // Split by '-'
    return `${month}/${day}/${year}`; // Rearrange to 'mm/dd/yyyy'
  };

  const formatDateRange = () => {
    // Get the fresh date range from the negotiation object instead of stale localStorage
    const freshDateRange = getNegotiationObj?.earliestCutoffDate && getNegotiationObj?.latestCutoffDate 
      ? [getNegotiationObj.earliestCutoffDate, getNegotiationObj.latestCutoffDate]
      : null;
    
    // Fall back to localStorage if no fresh data available
    const dateRange = freshDateRange || advancedSettings?.dateRange;
    
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return 'No date range set';
    }
    
    const startDate = dayjs(dateRange[0]).format('MM/DD/YYYY');
    const endDate = dayjs(dateRange[1]).format('MM/DD/YYYY');
    return `${startDate} - ${endDate}`;
  };

  // Get the fresh eval date from the negotiation object instead of stale store
  const freshEvalDate = getNegotiationObj?.evalDate || getEvalDate;
  const formattedEvalDate = formatEvalDate(freshEvalDate);
  


  const handleToolbarTabsChange = useCallback((event, newIdx) => {
    const item = tabItems[newIdx];
    if (!item) return;
    viewCallback(item.callbackValue);
  }, [tabItems, viewCallback]);

  const getUserNameById = useCallback((userId) => {
    if (!userId) return 'Unknown';
    
    // Find the user in userObject by matching userId
    const userEntry = Object.values(userObject).find(u => u.userId === userId);
    return userEntry ? userEntry.userName : 'Unknown';
  }, []);

  const offerAV = useMemo(() => {
    // strip out any commas (just in case)
    const raw = (displayOfferValue || '').toString().replace(/,/g, '');
    const numeric = parseFloat(raw);
    if (isNaN(numeric) || numeric <= 0) return 'N/A';
    return Math.round(numeric * getRar).toLocaleString();
  }, [displayOfferValue, getRar]);

  return (
      <div className="relative overflow-visible bg-[#f6f7f9] border-t border-[#dfe3e8] shadow-[0_-1px_4px_rgba(16,24,40,0.06)]" style={{
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        height: '40px',
        overflow: 'visible',
      }}>
        <div className='flex flex-grow'>
        </div>
    
        <div className="flex content-center items-center justify-between h-full">
          <Box
            className="flex w-1/3"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              height: '100%',
              alignSelf: 'stretch',
            }}
          >
            <Tabs
              value={selectedTabIndex}
              onChange={handleToolbarTabsChange}
              aria-label="basic tabs example"
              sx={{
                minHeight: '30px',
                borderBottom: 1,
                borderColor: 'divider',
                alignItems: 'flex-start',
              }}
              TabIndicatorProps={{
                sx: {
                  height: '2px',
                },
              }}
            >
              {tabItems.map((item) => {
                const tabSx = {
                  minHeight: '30px',
                  padding: '2px 12px',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: blue[700],
                    backgroundColor: '#f0f0f0',
                  },
                  '&.Mui-selected': {
                    color: blue[700],
                  },
                };
                if (item.id === 'town' && !hasTownTab) {
                  return <DisabledTownTabWithHover key={item.id} tabSx={tabSx} />;
                }
                return (
                  <Tab
                    key={item.id}
                    label={item.label}
                    sx={tabSx}
                  />
                );
              })}
            </Tabs>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: 1,
                height: '100%',
              }}
            >
              <Tooltip title={notesTooltipTitle}>
                <IconButton
                  onClick={handleNotesIconClick}
                  sx={{
                    color: hasAnyNotes ? blue[600] : 'text.secondary',
                    border: hasAnyNotes ? `1px solid ${blue[600]}` : '1px solid transparent',
                    borderRadius: '4px',
                    padding: '2px',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      color: blue[700],
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <EditNoteIcon sx={{ fontSize: '22px' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

        <div className="flex-1 justify-center flex items-center min-w-0 overflow-visible">
          <ToolbarValueDisplay
            selectedKey={selectedKey}
            ifmv={subjectIFMV}
            handleSelectChange={handleSelectChange}
            compAvg={compAvg}
            pctAdj={pctAdj}
            pctAdjClass={pctAdjClass}
            currentOfferValue={displayOfferValue}
            offerInputRef={offerInputRef}
            debouncedOfferPct={debouncedOfferPct}
            offerAV={offerAV}
            reviewDetails={reviewDetails}
            getUserNameById={getUserNameById}
            view={view}
            getNegotiationObj={getNegotiationObj}
            comp={comp}
            townOfferValue={displayTownOfferValue}
            townOfferInputRef={townOfferInputRef}
            isSettled={isSettled}
          />
          
          {/* Case Strength Widget - immediately to the right of value display */}
          <div className="hidden lg:block">
            <CaseStrengthWidget
              getNegotiationObj={getNegotiationObj}
              comp={comp}
            />
          </div>
          
        </div>

          <div className="w-1/3 flex justify-end text-right">
            {/* Case Timer Widget - before eval date and adjustments */}
            {/* <div className="hidden lg:flex items-center mr-2">
              <CaseTimer
                ifmv={subjectIFMV}
                caseId={caseId}
                comp={comp}
              />
            </div> */}

            <div className='text-sm mr-2 hidden lg:block'>
              {(() => {
                const eqMissing = getEq === null || getEq === undefined || getEq === '';
                return (
                  <div
                    className='flex justify-end items-center text-xs'
                    style={{
                      boxShadow: `inset 0 0 0 1px ${eqMissing ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'}`,
                      borderRadius: '4px',
                      padding: '4px 8px'
                    }}
                  >
                <Tooltip 
                  title={
                    <div>
                      <div style={{ textAlign: 'center' }}>
                        {eqMissing && (
                          <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#d32f2f' }}>
                            EQ does NOT EXIST
                          </div>
                        )}
                        RAR: {getRar}{' '}
                        <span style={eqMissing ? { border: '1px solid #d32f2f', padding: '2px 4px', borderRadius: '2px' } : undefined}>
                          EQ: {eqMissing ? '' : getEq}
                        </span>
                      </div>
                      <div>Date Range: {formatDateRange()}</div>
                    </div>
                  } 
                  placement="top"
                >
                  <InfoOutlinedIcon 
                    sx={{ 
                      fontSize: '14px', 
                      color: eqMissing ? '#d32f2f' : 'text.secondary',
                      mr: 0.5
                    }} 
                  />
                </Tooltip>
                <span> <strong>Eval Date:</strong> {formattedEvalDate}</span>
              </div>
                );
              })()}
              {/* <div className="flex justify-end items-center text-xs space-x-2">
                <span>
                  <strong>RAR:</strong> {getRar}
                </span>
                <span>
                  <strong>EQ:</strong> {getEq}
                </span>
              </div> */}
              <AdjustmentsSummaryDisplay adjustments={summaryAdjustments} sqftDisplay={memoizedSqftAdjustment} />
            </div>

            <SvgIconButton
              icon={SettingsIcon}
              onClick={handleClickOpen}
              tooltip="Advanced Settings"
              className="p-1"
              activeColor="text.secondary"
              hoverColor="primary.main"
              sx={{ padding: '4px' }}
            />
            {open &&
              <AdvancedSettings
              open={open}
              onClose={handleClose}
              onConfirm={handleSettingsConfirm}
              initialSettings={advancedSettings}
              runAnalysis={handleProcessDBUpdate}
              evalDate={evalDate || null}
              isLoadingEvalDate={isLoadingEvalDate || false}
              currentParcelId={getNegotiationObj?.cases?.[comp]?.parcel_id}
            />
            }

            <CompNavigation
              handleUpdateStateCallback={handleCompChangeWithSync}
            />
          </div>
        </div>

    </div>
  );
};


export default React.memo(TableCustomToolbar);